<template>
  <div class="payment-button-wrapper">
    <!-- SDK 加载中：session 未就绪且未失败时显示 loading（而非降级按钮），避免进弹窗瞬间误点跳转 -->
    <div v-if="!forceSkipSdk && !sdkState.loaded && !sdkFailed" class="sdk-loading">
      <van-loading size="20px" color="#FF1979"></van-loading>
    </div>

    <!-- SDK 双按钮容器：order(一次性) + subscription(订阅) 各建一个常驻 PM 按钮，按当前类型 v-show 切换，跨类型不重建 -->
    <div v-else-if="!forceSkipSdk && sdkState.loaded && !sdkFailed" class="sdk-button-box">
      <!-- 首建期间 loading 占位，避免 iframe 渲染空窗期闪动 -->
      <div v-show="btnMounting" class="sdk-mounting">
        <van-loading size="20px" color="#FF1979"></van-loading>
      </div>
      <div v-show="!isSubscribe" :id="orderContainerId" class="sdk-button-container" :class="{ 'apple-frame': paymentMethod === 'applepay' }" />
      <div v-show="isSubscribe" :id="subContainerId" class="sdk-button-container" :class="{ 'apple-frame': paymentMethod === 'applepay' }" />
    </div>

    <!-- 降级按钮（SDK 未就绪/渲染失败/强制跳过时显示，点击走原有跳转模式） -->
    <div v-else class="fallback-button" @click="handleFallback">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

interface SdkState {
  loading: boolean
  loaded: boolean
  error: any
}

interface Props {
  // 支付渠道：googlepay / applepay（card 走全屏，paypal 走跳转，均不经过此组件）
  paymentMethod: string
  sdkState: SdkState
  // 商品（下单用，需 rsp_id）
  product?: any
  // 下单附加参数（book_id/chapter_id/pay_entry）
  extra?: any
  // 支付类型：order=一次性，subscription=订阅
  paymentType?: string
  // 订阅管理地址（订阅场景使用）
  mitManagementUrl?: string
  // 支付成功重定向地址
  returnUrl?: string
  forceSkipSdk?: boolean  // 强制跳过 SDK，直接降级
}

const props = withDefaults(defineProps<Props>(), {
  product: () => ({}),
  extra: () => ({}),
  paymentType: 'order',
  mitManagementUrl: '',
  returnUrl: '',
  forceSkipSdk: false
})

const emit = defineEmits<{
  fallback: []
  'payment-failed': [msg: string]
  // 原生弹窗授权通过后到跳转前（下单 + 建支付请求两次请求）通知外层开全屏 loading，避免界面无反馈像卡住
  paying: [v: boolean]
}>()

// 前置组件按类型分桶自取 session（order 一份 / subscription 一份，各 60 分钟复用）
const { loadPayermaxSdk, getCachedSession, getOrCreateSession, clearCachedSession } = usePaymentPreload()
// 前置组件下单走 createDropInOrder（/order/create，与收银台同接口）
const { createDropInOrder, createDropInPayment } = usePay()

// 容器唯一 id，避免多实例（多个 PaymentButton）冲突。双容器固定 id：切换靠 v-show，不换元素、不重建
const instanceId = `${props.paymentMethod}-${Math.floor(Math.random() * 1e9)}`
const orderContainerId = computed(() => `pm-btn-order-${instanceId}`)
const subContainerId = computed(() => `pm-btn-sub-${instanceId}`)

// 当前支付类型是否订阅（与 popupHandle 的 product_type===1?'order':'subscription' 一致）
const isSubscribe = computed(() => props.paymentType === 'subscription')
const bucketType = (isSub: boolean) => (isSub ? 'subscription' : 'order')

// PayerMax 子渠道标识（createDropInPayment 用）
const payChannel = computed(() =>
  props.paymentMethod === 'googlepay' ? 'googlepay-payermax' : 'applepay-payermax'
)

// 双桶状态：btn 实例 / 就绪 / 建中锁 / token 失效重试标志（各桶只重试一次防死循环）
let orderBtn: any = null
let subBtn: any = null
const orderReady = ref(false)
const subReady = ref(false)
let orderBuilding = false
let subBuilding = false
let orderRetried = false
let subRetried = false

// 首建 mount 中（iframe 渲染空窗期显示 loading 占位，避免闪动）
const btnMounting = ref(false)
// SDK 渲染失败 → 切回降级按钮（点击走原跳转收银台）
const sdkFailed = ref(false)
// 支付进行中标志：防止用户重复点击 SDK 按钮造成重复下单
const isPaying = ref(false)

// —— 双桶访问器 ——
const getReady = (isSub: boolean) => (isSub ? subReady.value : orderReady.value)
const setBtn = (isSub: boolean, btn: any) => {
  if (isSub) { subBtn = btn; subReady.value = !!btn }
  else { orderBtn = btn; orderReady.value = !!btn }
}
const getBuilding = (isSub: boolean) => (isSub ? subBuilding : orderBuilding)
const setBuilding = (isSub: boolean, v: boolean) => {
  if (isSub) subBuilding = v; else orderBuilding = v
}
const getRetried = (isSub: boolean) => (isSub ? subRetried : orderRetried)
const setRetried = (isSub: boolean, v: boolean) => {
  if (isSub) subRetried = v; else orderRetried = v
}
// 该桶是否当前可见类型（决定失败时降级 or 仅标记未就绪）
const isVisible = (isSub: boolean) => isSub === isSubscribe.value
// 可见类型渲染失败 → 整组降级为跳转：sdkFailed 会把按钮盒（含两容器）从 DOM 移除，
// 故同时复位两桶状态，保证之后切回时（sdkFailed 复位）用全新空容器重建，不复用失效实例
const downgrade = () => {
  sdkFailed.value = true
  orderBtn = null
  subBtn = null
  orderReady.value = false
  subReady.value = false
  orderBuilding = false
  subBuilding = false
  btnMounting.value = false  // 建中占位一并复位，否则之后切类型重置 sdkFailed 时会残留一层 loading 遮罩
}
// 复位双桶状态（session 被清、按钮盒卸载时用）：容器与 PM 按钮已随盒子一并销毁，
// 状态若不复位则下次 loaded 翻 true 命中 ready 提前 return → 空白；retried/sdkFailed 一并归零
const resetBuckets = () => {
  orderBtn = null
  subBtn = null
  orderReady.value = false
  subReady.value = false
  orderBuilding = false
  subBuilding = false
  orderRetried = false
  subRetried = false
  sdkFailed.value = false
  btnMounting.value = false
}
// 清空指定桶容器 DOM（移除旧按钮 iframe）；重建前必须先清，避免同元素二次 mount
const clearBucketContainer = (isSub: boolean) => {
  const c = document.getElementById(isSub ? subContainerId.value : orderContainerId.value)
  if (c) c.innerHTML = ''
}
// 空闲执行（后台补建另一类型用）
const runIdle = (fn: () => void) => {
  const ric = (window as any).requestIdleCallback
  if (typeof ric === 'function') ric(fn)
  else setTimeout(fn, 200)
}

// 拼接成功回调地址（与信用卡全屏一致，复用现有轮询逻辑）
const buildSuccessUrl = (orderNo: string, type: string) => {
  const sep = props.returnUrl.includes('?') ? '&' : '?'
  const params = new URLSearchParams({
    order_no: orderNo,
    payment_type: type,
    pay_channel: payChannel.value,
    status: 'SUCCESS',
    commit: 'confirm'
  })
  return `${props.returnUrl}${sep}${params.toString()}`
}

// 拼接取消回调地址（status=CLOSED），复用现有 RechargeFailed 挽留 + 上报链路
const buildCancelUrl = (orderNo: string, type: string) => {
  const sep = props.returnUrl.includes('?') ? '&' : '?'
  const params = new URLSearchParams({
    order_no: orderNo,
    payment_type: type,
    pay_channel: payChannel.value,
    status: 'CLOSED'
  })
  return `${props.returnUrl}${sep}${params.toString()}`
}

// 建单个 PM 按钮（order/subscription 桶），mount 到对应容器并绑 payButtonClick
// isPrimary：是否当前可见类型（失败时降级由 onFail 决定）。返回 btn（失败返 null）
const createAndMountBtn = async (isSub: boolean, session: any): Promise<any> => {
  const cid = isSub ? subContainerId.value : orderContainerId.value
  const container = document.getElementById(cid)
  if (!container) return null
  container.style.opacity = '0'  // mount 前透明，避免 iframe 渲染空窗期闪动（对齐 master）

  const PM = (window as any).PMdropin
  if (!PM) return null

  let btn: any = null
  if (props.paymentMethod === 'googlepay') {
    btn = PM.create('googlepay', {
      clientKey: session.client_key,
      sessionKey: session.session_key,
      language: 'en',
      sandbox: session.sandbox,
      payButtonConfig: {
        buttonRadius: '12',
        buttonColor: 'white',
        buttonLocale: 'en',
        width: '100%',
        height: '55px'
      }
    })
  } else {
    btn = PM.create('applepay', {
      clientKey: session.client_key,
      sessionKey: session.session_key,
      theme: 'dark',
      language: 'en',
      sandbox: session.sandbox,
      payButtonStyle: 'width:100%;height:55px;border-radius:12px;padding:0rem !important;max-height:55px;background:#F7F7F7;'
    })
  }
  if (!btn) return null

  btn.mount(container)
  setTimeout(() => { container.style.opacity = '1' }, 50)  // mount 后恢复可见（对齐 master）

  // load 失败/超时处理（每桶独立）：token 失效清桶重建一次；否则可见类型降级、后台类型仅标记未就绪
  const onFail = (reason: string, code?: string) => {
    const isTokenExpired = code === 'TOKEN_ERROR' || /expired/i.test(reason)
    if (isTokenExpired && !getRetried(isSub)) {
      setRetried(isSub, true)
      clearCachedSession(bucketType(isSub))
      clearBucketContainer(isSub)
      setBtn(isSub, null)
      // 重建用商品 id：仅当该桶为当前可见类型时，props.product.rsp_id 才对应该类型
      // 后台（非可见）桶无从 props 取其 rsp_id → 跳过重建，切到该类型时由 watch 懒建
      const rspId = props.product?.rsp_id
      if (!isVisible(isSub) || !rspId) {
        if (isVisible(isSub)) downgrade()
        return
      }
      nextTick(async () => {
        try {
          const fresh = await getOrCreateSession(bucketType(isSub), rspId, props.extra)
          if (!fresh) { downgrade(); return }
          const newBtn = await createAndMountBtn(isSub, fresh)
          if (newBtn) setBtn(isSub, newBtn)
          else downgrade()
        } catch (e) {
          console.error('[PaymentButton] token 失效重建失败，降级:', e)
          downgrade()
        }
      })
      return
    }
    // 非 token 失败 / 已重试过：可见类型降级，后台类型仅标记未就绪
    if (isVisible(isSub)) downgrade()
    else setBtn(isSub, null)
  }

  let loadFired = false
  btn.on('load', (res: any = {}) => {
    loadFired = true
    if (isVisible(isSub)) btnMounting.value = false
    if (res?.code !== 'SUCCESS') {
      console.error('[PaymentButton] SDK 渲染失败:', res)
      onFail('SDK 渲染失败: ' + (res?.msg || JSON.stringify(res)), res?.code)
    }
  })
  // 兜底：10s 内仍未触发 load（SDK 卡死/网络慢）→ 失败处理，避免空白卡死
  setTimeout(() => {
    if (isVisible(isSub)) btnMounting.value = false
    if (!loadFired) {
      console.error('[PaymentButton] SDK load 超时未触发')
      onFail('SDK load 超时(10s)未触发')
    }
  }, 10000)

  // 点击 SDK 原生按钮 → 用该按钮自己的类型 + session 下单支付
  btn.on('payButtonClick', () => handlePay(bucketType(isSub), session))

  return btn
}

// 初始化：先建当前类型按钮，再空闲后台补建另一类型（只用缓存，无缓存留待切换懒建）
const initButtons = async () => {
  if (props.forceSkipSdk) return
  const currentSub = isSubscribe.value
  // 当前桶已建/建中 → 跳过（onMounted 与 watch(loaded) 可能并发触发）
  if (getReady(currentSub) || getBuilding(currentSub)) return
  const rspId = props.product?.rsp_id
  if (!rspId) return
  setBuilding(currentSub, true)
  try {
    const sdkLoaded = await loadPayermaxSdk()
    if (!sdkLoaded) { downgrade(); return }
    // 当前类型 session：命中缓存直接用，否则现建（写入对应桶）再取
    const session = await getOrCreateSession(bucketType(currentSub), rspId, props.extra)
    if (!session) { downgrade(); return }
    btnMounting.value = true
    // 等两个容器都渲染到位（loading 遮罩关掉后需一帧让容器进入 DOM）
    await nextTick()
    await new Promise((r) => requestAnimationFrame(() => r(null)))
    const btn = await createAndMountBtn(currentSub, session)
    if (btn) setBtn(currentSub, btn)
    else downgrade()
  } catch (e) {
    console.error('[PaymentButton] 初始化失败，降级跳转:', e)
    downgrade()
  } finally {
    setBuilding(currentSub, false)
  }
  // 后台补建另一类型（仅当前建成、未降级时）
  if (!sdkFailed.value) runIdle(() => buildOtherFromCache())
}

// 后台补建另一类型：只读缓存（不发请求）；无缓存跳过，切换时再懒建
const buildOtherFromCache = async () => {
  const otherSub = !isSubscribe.value
  if (getReady(otherSub) || getBuilding(otherSub)) return
  const session = getCachedSession(bucketType(otherSub))
  if (!session) return
  setBuilding(otherSub, true)
  try {
    const btn = await createAndMountBtn(otherSub, session)
    if (btn) setBtn(otherSub, btn)
  } catch (e) {
    console.warn('[PaymentButton] 后台补建另一类型失败:', e)
  } finally {
    setBuilding(otherSub, false)
  }
}

// 懒建目标类型（切换到未就绪桶时用）：切换后 props.product 已是目标类型商品，rsp_id 正确
const ensureBucketBuilt = async (isSub: boolean) => {
  if (getReady(isSub) || getBuilding(isSub)) return
  const rspId = props.product?.rsp_id
  if (!rspId) return
  setBuilding(isSub, true)
  // 建 session 前就亮占位（切来的目标类型即当前可见类型）：此时外层已就绪走按钮盒分支，
  // 不占位则整个建桶期间（含 session 请求往返）该容器是空白的
  btnMounting.value = true
  try {
    const session = await getOrCreateSession(bucketType(isSub), rspId, props.extra)
    if (!session) { downgrade(); return }
    await nextTick()
    const btn = await createAndMountBtn(isSub, session)
    if (btn) setBtn(isSub, btn)
    else downgrade()
  } catch (e) {
    console.error('[PaymentButton] 懒建失败，降级:', e)
    downgrade()
  } finally {
    setBuilding(isSub, false)
  }
}

// SDK 按钮点击：下单（/order/create）→ canMakePayment → createDropInPayment → 跳转
// type/session 由被点按钮显式传入（点谁走谁的类型/会话）
const handlePay = async (type: string, session: any) => {
  if (isPaying.value) return  // 防重复点击：支付进行中直接忽略，避免重复下单
  isPaying.value = true
  let redirecting = false  // 已发起整页跳转：finally 里不关遮罩，否则导航完成前会露出弹窗界面
  try {
    // 1. 立即发起下单（拿 Promise 不 await）——避免 await 打断用户手势上下文
    //    Apple Pay 要求 ApplePaySession 必须在手势同步栈内创建，await 网络请求会破坏该上下文
    const orderPromise = createDropInOrder(props.product, props.extra, payChannel.value)

    // 2. 同步调 canMakePayment（仍在手势栈内）→ 取 payment_token / 创建 ApplePaySession
    // 订阅场景：传 subscriptionPlan（用商品的 subscription_plan_config.payermax 官方原始结构，整体透传）
    // 一次性场景：后端建 session 未传金额，前端补 totalAmount（纯数字字符串，用于 Apple Pay 弹窗展示；须与最终下单金额一致）
    const totalAmount = String(props.product?.discount_price || props.product?.price || '')
    const canMakePaymentParams = type === 'subscription' && props.product?.subscription_plan_config?.payermax
      ? {
        mitManagementUrl: props.mitManagementUrl,
        subscriptionPlan: props.product.subscription_plan_config.payermax
      }
      : { totalAmount }
    const btn = type === 'subscription' ? subBtn : orderBtn
    const res = await btn.emit('canMakePayment', canMakePaymentParams)

    if (res.code === 'APPLY_SUCCESS') {
      // 授权已通过，后续下单 + 建支付请求两次网络往返期间原生弹窗已消失，界面无反馈 → 通知外层开全屏 loading
      emit('paying', true)
      // 3. canMakePayment 成功后，再 await 下单结果，拿正式业务订单号
      const orderRes: any = await orderPromise
      if (!orderRes || orderRes.code !== 0) {
        emit('payment-failed', 'Failed to create order')
        return
      }
      const orderNo = orderRes.data.order_no

      // 4. 创建前置组件支付请求
      const payRes: any = await createDropInPayment({
        order_no: orderNo,
        session_key: session.session_key,
        payment_token: res.data.paymentToken,
        pay_channel: payChannel.value,
        payment_type: type,
        mit_management_url: props.mitManagementUrl,
        return_url: props.returnUrl
      })

      if (payRes.code === 0) {
        // 有 approve_url（3DS 挑战页，订阅场景才有）→ SDK 弹框处理 3DS
        if (payRes.data.approve_url) {
          btn.create3DSPopup({ url: payRes.data.approve_url })
            .then((result: any) => {
              if (result.code === '3DS_PROCESSED') {
                window.location.href = buildSuccessUrl(orderNo, type)
              }
            })
            .catch((err: any) => {
              if (err.code === 'USER_CANCEL') {
                // 用户取消 3DS → 跳转 status=CLOSED，触发挽留 + 上报
                window.location.href = buildCancelUrl(orderNo, type)
              } else {
                emit('payment-failed', err.msg || '3DS authentication failed')
              }
            })
          return
        }
        // 无 approve_url（一次性支付直接成功）→ 跳转成功回调，复用现有轮询逻辑
        redirecting = true
        window.location.href = buildSuccessUrl(orderNo, type)
      } else {
        emit('payment-failed', payRes.msg || 'Payment failed')
      }
    } else if (res.code === 'CANCELED' || res.code === 'APPLE_PAY_CANCEL') {
      // 用户取消原生弹窗（Google=CANCELED，Apple=APPLE_PAY_CANCEL）→ 跳 status=CLOSED，触发挽留 + 上报（同收银台取消）
      // 取消已脱离手势栈，可安全 await 下单结果拿 order_no
      const orderRes: any = await orderPromise
      window.location.href = buildCancelUrl(orderRes?.data?.order_no || '', type)
    } else {
      // 其他非成功 → 支付失败/不可用 → 停在弹窗提示，用户可重试
      emit('payment-failed', res.msg || 'Payment not available')
    }
  } catch (error: any) {
    console.error('[PaymentButton] 支付失败:', error)
    emit('payment-failed', error.message || 'Payment failed')
  } finally {
    isPaying.value = false  // 释放锁，允许重试（失败/取消后用户可再点）
    if (!redirecting) emit('paying', false)  // 跳转中保持遮罩，其余路径（失败/取消/3DS 接管）关掉
  }
}

// 降级按钮点击
const handleFallback = () => {
  emit('fallback')
}

// SDK 就绪（loaded=true）→ 建按钮；session 被清（loaded=false，如 initDropIn 换商品重建 session）
// → 按钮盒随之卸载、容器与 PM 按钮一并销毁，此时必须复位双桶，否则 loaded 再翻 true 时
// initButtons 命中 stale ready 提前 return → 挂进全新空容器的按钮建不出来 → 空白。
// 注意 loaded 只跟 session 走、不跟 isShow：开关弹窗不动 session → 不触发复位 → 复用不重渲染。
watch(
  () => props.sdkState.loaded,
  (loaded) => {
    if (props.forceSkipSdk) return
    if (loaded) {
      initButtons()
    } else {
      resetBuckets()
    }
  }
)

// 切换支付类型 / 商品：跨类型靠 v-show 切容器（不重建），目标桶未就绪则懒建；同类型切商品复用不重建
watch(
  () => [props.paymentType, props.product?.rsp_id],
  ([newType], [oldType]) => {
    if (props.forceSkipSdk || !props.sdkState.loaded) return
    const newSub = newType === 'subscription'
    const oldSub = oldType === 'subscription'
    if (newSub !== oldSub) {
      sdkFailed.value = false  // 切到另一类型，重置降级标记（新类型可能可用）
      if (!getReady(newSub)) ensureBucketBuilt(newSub)
    }
    // 同类型切商品：session 复用、按钮不重建；订阅计划取自 props.product.subscription_plan_config.payermax（已随 props 更新）
  }
)

onMounted(() => {
  if (props.sdkState.loaded && !props.forceSkipSdk) {
    initButtons()
  }
})

onBeforeUnmount(() => {
  clearBucketContainer(false)
  clearBucketContainer(true)
  orderBtn = null
  subBtn = null
})
</script>

<style scoped lang="scss">
.payment-button-wrapper {
  width: 100%;

  .sdk-loading {
    width: 100%;
    height: 55px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border-radius: 12px;
  }

  .sdk-button-box {
    position: relative;
    width: 100%;
    margin-bottom: 12px;
  }

  .sdk-mounting {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border-radius: 12px;
    z-index: 1;
  }

  .sdk-button-container {
    width: 100%;
    height: 55px;
    min-height: 55px;
    overflow: hidden;
  }

  // Apple Pay：SDK 原生按钮默认带黑色 frame 内边距，需用白色圆角框包裹并清除 frame 内边距（对齐 master-h5）
  .sdk-button-container.apple-frame {
    border: 1px solid #555;
    border-radius: 12px;
    background-color: #ffffff;
    --padding-frame: 0;
  }

  .fallback-button {
    width: 100%;
    cursor: pointer;
  }
}
</style>
